"use client"

import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeader } from '../utils/auth';
import { useOutletContext } from 'react-router-dom';
import { isEnglishOperatorMode } from '../utils/operatorMode';

// --- Icons ---
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>;
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;
const PlayIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>;
const PauseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>;
const ClockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>;
const MailIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>;
const ArrowDownIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></svg>;
const ConditionIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>;

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

const SCENARIO_COPY = {
    ja: {
        pageTitle: "メールオートメーション (シナリオ)",
        pageLead: "フォーム送信、タグ付与、スコア到達をトリガーに、ステップメールを自動配信します。",
        create: "シナリオ作成",
        loading: "シナリオを読み込み中...",
        empty: "シナリオがありません。「シナリオ作成」から自動化を始めましょう。",
        active: "稼働中",
        paused: "停止中",
        startCondition: "開始条件",
        steps: "ステップ数",
        updated: "最終更新",
        edit: "編集",
        delete: "削除",
        headers: ["ステータス", "シナリオ名", "開始条件 (トリガー)", "ステップ数", "最終更新", "操作"],
        deletedForm: "(削除されたフォーム)",
        allMetaForms: "全てのフォーム",
        triggerText: {
            form: (title) => `フォーム送信: ${title}`,
            meta: (detail) => `Meta広告: ${detail}`,
            tag: (detail) => `タグ付与: ${detail}`,
            score: (detail) => `スコア${detail}点以上`,
            manual: "手動実行のみ",
        },
        alerts: {
            detailFailed: "詳細の取得に失敗しました",
            network: "通信エラー",
            deleteConfirm: "このシナリオを削除しますか？\n(稼働中の場合、処理は停止します)",
            deleteFailed: "削除に失敗しました",
        },
        campaignStatus: {
            draft: "下書き",
            scheduled: "予約中",
            sending: "送信中",
            completed: "配信済",
            cancelled: "キャンセル",
            fallback: "メール",
        },
        modal: {
            editTitle: "シナリオ編集",
            newTitle: "新規シナリオ作成",
            basics: "基本設定 & トリガー",
            name: "シナリオ名",
            namePlaceholder: "例: 資料請求後のフォローアップ",
            description: "説明 (管理用)",
            descriptionPlaceholder: "このシナリオの目的など",
            trigger: "開始トリガー (きっかけ)",
            triggerOptions: {
                form_submission: "フォーム送信時 (LPなど)",
                meta_ad_submission: "Meta広告 (インスタントフォーム) 受信時",
                tag_added: "タグが付与された時",
                score_reached: "スコアが一定以上になった時",
                manual: "手動登録のみ",
            },
            targetForm: "対象フォーム",
            select: "-- 選択してください --",
            targetFormId: "対象フォームID (任意)",
            targetFormIdPlaceholder: "空欄の場合は全てのMeta広告が対象",
            targetFormIdNote: "※特定の広告フォームのみをトリガーにする場合はIDを入力してください。通常は空欄で問題ありません。",
            targetTag: "対象タグ名",
            targetTagPlaceholder: "例: 2024展示会",
            targetTagNote: "※ リード管理のタグ欄、手動登録、CSVインポートで付与されたリードタグが対象です。",
            targetScore: "対象スコア",
            targetScorePlaceholder: "例: 50",
            targetScoreNote: "※ リードスコアがこの点数以上になった時に開始します。",
            stepSettings: "配信ステップ設定",
            condition: "実行条件:",
            conditions: {
                always: "無条件で実行 (直前のステップ完了後)",
                if_opened: "メールを開封したら",
                if_not_opened: "メールが未開封なら",
                if_clicked: "メール内のリンクをクリックしたら",
                if_not_clicked: "メール内のリンクを一度もクリックしてなければ",
            },
            reference: "参照対象:",
            referenceStep: (step) => `ステップ ${step} の結果を参照`,
            referenceNote: (step) => `※ステップ ${step} のメールへの反応をチェックします。`,
            waitTime: "待機時間 (直前から)",
            hoursAfter: "時間後",
            immediateNote: "※0時間は即時配信",
            emailSource: "送信するメール (過去のキャンペーンから選択)",
            campaignSelect: "-- 配信履歴から選択 --",
            campaignNote: "※ 過去にメール配信機能で作成・送信した文面を使用します。",
            addStep: "ステップを追加",
            activate: "すぐに稼働させる (有効化)",
            cancel: "キャンセル",
            save: "保存する",
        },
    },
    en: {
        pageTitle: "Scenario Campaigns",
        pageLead: "Automate step emails from form submissions, tags, score thresholds, Meta instant forms, or manual enrollment.",
        create: "Create Scenario",
        loading: "Loading scenarios...",
        empty: "No scenarios yet. Create a scenario to start automation.",
        active: "Active",
        paused: "Paused",
        startCondition: "Start condition",
        steps: "Steps",
        updated: "Last updated",
        edit: "Edit",
        delete: "Delete",
        headers: ["Status", "Scenario", "Start Condition", "Steps", "Last Updated", "Actions"],
        deletedForm: "(deleted form)",
        allMetaForms: "all forms",
        triggerText: {
            form: (title) => `Form submission: ${title}`,
            meta: (detail) => `Meta Ads: ${detail}`,
            tag: (detail) => `Tag added: ${detail}`,
            score: (detail) => `Score ${detail}+`,
            manual: "Manual enrollment only",
        },
        alerts: {
            detailFailed: "Failed to load details",
            network: "Network error",
            deleteConfirm: "Delete this scenario?\nIf it is active, automation will stop.",
            deleteFailed: "Delete failed",
        },
        campaignStatus: {
            draft: "Draft",
            scheduled: "Scheduled",
            sending: "Sending",
            completed: "Delivered",
            cancelled: "Canceled",
            fallback: "Email",
        },
        modal: {
            editTitle: "Edit Scenario",
            newTitle: "Create Scenario",
            basics: "Basic Settings & Trigger",
            name: "Scenario Name",
            namePlaceholder: "Example: Follow-up after guide request",
            description: "Description (internal)",
            descriptionPlaceholder: "Purpose of this scenario",
            trigger: "Start Trigger",
            triggerOptions: {
                form_submission: "Form submission (LP, etc.)",
                meta_ad_submission: "Meta Ads instant form received",
                tag_added: "Tag added",
                score_reached: "Score reaches threshold",
                manual: "Manual enrollment only",
            },
            targetForm: "Target Form",
            select: "-- Select --",
            targetFormId: "Target Form ID (optional)",
            targetFormIdPlaceholder: "Leave blank to include all Meta ad forms",
            targetFormIdNote: "Enter an ID only when you want a specific ad form to trigger this scenario. Blank is usually fine.",
            targetTag: "Target Tag",
            targetTagPlaceholder: "Example: 2026 webinar",
            targetTagNote: "Uses lead tags added in Lead Management, manual add, or CSV import.",
            targetScore: "Target Score",
            targetScorePlaceholder: "Example: 50",
            targetScoreNote: "Starts when a lead score becomes equal to or higher than this value.",
            stepSettings: "Delivery Step Settings",
            condition: "Condition:",
            conditions: {
                always: "Always run after the previous step completes",
                if_opened: "If the email was opened",
                if_not_opened: "If the email was not opened",
                if_clicked: "If a link in the email was clicked",
                if_not_clicked: "If no link in the email was clicked",
            },
            reference: "Reference:",
            referenceStep: (step) => `Use result from step ${step}`,
            referenceNote: (step) => `Checks engagement for the email in step ${step}.`,
            waitTime: "Wait Time (after previous step)",
            hoursAfter: "hours later",
            immediateNote: "0 hours sends immediately.",
            emailSource: "Email to Send (select from past campaigns)",
            campaignSelect: "-- Select from campaign history --",
            campaignNote: "Uses email content already created or sent in Email Campaigns.",
            addStep: "Add Step",
            activate: "Activate immediately",
            cancel: "Cancel",
            save: "Save",
        },
    },
};

const getScenarioCopy = (isEnglishBeta) => isEnglishBeta ? SCENARIO_COPY.en : SCENARIO_COPY.ja;

const formatCampaignOptionStatus = (status, isEnglishBeta = false) => {
    const statusCopy = getScenarioCopy(isEnglishBeta).campaignStatus;
    switch (status) {
        case 'draft':
            return statusCopy.draft;
        case 'scheduled':
            return statusCopy.scheduled;
        case 'sending':
            return statusCopy.sending;
        case 'completed':
            return statusCopy.completed;
        case 'cancelled':
            return statusCopy.cancelled;
        default:
            return statusCopy.fallback;
    }
};

const LoadingSpinner = ({ text = "読み込み中..." }) => (
    <div className="flex flex-col items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
        <p className="text-gray-500 text-sm">{text}</p>
    </div>
);

// シナリオ編集モーダル
function ScenarioEditorModal({ scenario, campaigns, forms, onClose, onSave, isEnglishBeta = false }) {
    const copy = getScenarioCopy(isEnglishBeta).modal;
    const [formData, setFormData] = useState({
        id: null,
        title: '',
        description: '',
        trigger_type: 'form_submission',
        trigger_detail: '',
        is_active: false,
        steps: [] // [{ wait_time_hours: 0, action_type: 'send_email', campaign_id: null, ... }]
    });

    useEffect(() => {
        if (scenario) {
            // 編集モード: 既存データをロード
            setFormData({
                id: scenario.id,
                title: scenario.title,
                description: scenario.description || '',
                trigger_type: scenario.trigger_type,
                trigger_detail: scenario.trigger_detail || '',
                is_active: scenario.is_active === 1,
                steps: (scenario.steps || []).map(s => ({
                    ...s,
                    // キャンペーンIDがあればセット
                    campaign_id: s.campaign_id || ''
                }))
            });
        } else {
            // 新規作成モード
            setFormData({
                id: null,
                title: '',
                description: '',
                trigger_type: 'form_submission',
                trigger_detail: forms.length > 0 ? forms[0].id : '',
                is_active: false,
                steps: [{ 
                    wait_time_hours: 0, 
                    action_type: 'send_email', 
                    condition_type: 'always',
                    campaign_id: campaigns.length > 0 ? campaigns[0].id : ''
                }]
            });
        }
    }, [scenario, campaigns, forms]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleTriggerChange = (e) => {
        const triggerType = e.target.value;
        const defaultDetail = triggerType === 'form_submission'
            ? (forms.length > 0 ? forms[0].id : '')
            : triggerType === 'score_reached'
                ? '50'
                : '';
        setFormData(prev => ({
            ...prev,
            trigger_type: triggerType,
            trigger_detail: defaultDetail
        }));
    };

    // ステップ操作
    const addStep = () => {
        setFormData(prev => ({
            ...prev,
            steps: [...prev.steps, { 
                wait_time_hours: 24, 
                action_type: 'send_email', 
                condition_type: 'always',
                campaign_id: campaigns.length > 0 ? campaigns[0].id : ''
            }]
        }));
    };

    const removeStep = (index) => {
        const newSteps = [...formData.steps];
        newSteps.splice(index, 1);
        setFormData(prev => ({ ...prev, steps: newSteps }));
    };

    const updateStep = (index, key, value) => {
        const newSteps = [...formData.steps];
        newSteps[index] = { ...newSteps[index], [key]: value };
        setFormData(prev => ({ ...prev, steps: newSteps }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="bg-white w-full max-w-4xl rounded-lg shadow-xl h-[92vh] sm:h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="font-bold text-lg text-gray-800">{scenario ? copy.editTitle : copy.newTitle}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><CloseIcon /></button>
                </div>
                
                <div className="flex-grow overflow-y-auto p-4 sm:p-6 bg-gray-50">
                    <form id="scenario-form" onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                        {/* 基本設定 */}
                        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                            <h4 className="font-bold text-gray-700 mb-4 border-b pb-2">{copy.basics}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1">{copy.name} <span className="text-red-500">*</span></label>
                                        <input type="text" name="title" required className="w-full p-2 border rounded" value={formData.title} onChange={handleChange} placeholder={copy.namePlaceholder} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1">{copy.description}</label>
                                        <textarea name="description" className="w-full p-2 border rounded" rows="2" value={formData.description} onChange={handleChange} placeholder={copy.descriptionPlaceholder}></textarea>
                                    </div>
                                </div>
                                <div className="space-y-4 bg-blue-50 p-4 rounded-md border border-blue-100">
                                    <div>
                                        <label className="block text-sm font-bold text-blue-800 mb-1">{copy.trigger}</label>
                                        <select name="trigger_type" className="w-full p-2 border rounded bg-white" value={formData.trigger_type} onChange={handleTriggerChange}>
                                            <option value="form_submission">{copy.triggerOptions.form_submission}</option>
                                            <option value="meta_ad_submission">{copy.triggerOptions.meta_ad_submission}</option>
                                            <option value="tag_added">{copy.triggerOptions.tag_added}</option>
                                            <option value="score_reached">{copy.triggerOptions.score_reached}</option>
                                            <option value="manual">{copy.triggerOptions.manual}</option>
                                        </select>
                                    </div>
                                    
                                    {formData.trigger_type === 'form_submission' && (
                                        <div>
                                            <label className="block text-sm font-bold text-blue-800 mb-1">{copy.targetForm}</label>
                                            <select name="trigger_detail" className="w-full p-2 border rounded bg-white" value={formData.trigger_detail} onChange={handleChange} required>
                                                <option value="">{copy.select}</option>
                                                {forms.map(f => (
                                                    <option key={f.id} value={f.id}>{f.title}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    {formData.trigger_type === 'meta_ad_submission' && (
                                        <div className="animate-fadeIn">
                                            <label className="block text-sm font-bold text-blue-800 mb-1">{copy.targetFormId}</label>
                                            <input 
                                                type="text" 
                                                name="trigger_detail" 
                                                className="w-full p-2 border rounded" 
                                                value={formData.trigger_detail} 
                                                onChange={handleChange} 
                                                placeholder={copy.targetFormIdPlaceholder}
                                            />
                                            <p className="text-[10px] text-blue-600 mt-1">
                                                {copy.targetFormIdNote}
                                            </p>
                                        </div>
                                    )}
                                    {formData.trigger_type === 'tag_added' && (
                                        <div>
                                            <label className="block text-sm font-bold text-blue-800 mb-1">{copy.targetTag}</label>
                                            <input type="text" name="trigger_detail" className="w-full p-2 border rounded" value={formData.trigger_detail} onChange={handleChange} placeholder={copy.targetTagPlaceholder} required />
                                            <p className="text-[10px] text-blue-600 mt-1">{copy.targetTagNote}</p>
                                        </div>
                                    )}
                                    {formData.trigger_type === 'score_reached' && (
                                        <div>
                                            <label className="block text-sm font-bold text-blue-800 mb-1">{copy.targetScore}</label>
                                            <input type="number" min="0" step="1" name="trigger_detail" className="w-full p-2 border rounded" value={formData.trigger_detail} onChange={handleChange} placeholder={copy.targetScorePlaceholder} required />
                                            <p className="text-[10px] text-blue-600 mt-1">{copy.targetScoreNote}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ステップ設定 (シナリオビルダー) */}
                        <div className="bg-gray-100 p-4 sm:p-6 rounded-lg border border-gray-300">
                            <h4 className="font-bold text-gray-700 mb-6 flex items-center gap-2">
                                <MailIcon /> {copy.stepSettings}
                            </h4>
                            
                            <div className="space-y-0">
                                {/* 開始地点 */}
                                <div className="flex justify-center mb-2">
                                    <div className="bg-gray-600 text-white text-xs font-bold px-3 py-1 rounded-full">START</div>
                                </div>
                                <div className="flex justify-center mb-2"><div className="h-6 w-px bg-gray-300"></div></div>

                                {formData.steps.map((step, idx) => (
                                    <React.Fragment key={idx}>
                                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 relative group transition-all hover:shadow-md">
                                            {/* 条件分岐設定エリア */}
                                            {idx > 0 && (
                                                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm space-y-3">
                                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                                                        <div className="flex items-center gap-1 font-bold text-yellow-700 flex-shrink-0">
                                                            <ConditionIcon /> {copy.condition}
                                                        </div>
                                                        <select
                                                            className="flex-grow p-1.5 border border-yellow-300 rounded bg-white text-gray-700 font-bold"
                                                            value={step.condition_type || 'always'}
                                                            onChange={(e) => updateStep(idx, 'condition_type', e.target.value)}
                                                        >
                                                            <option value="always">{copy.conditions.always}</option>
                                                            <option value="if_opened">{copy.conditions.if_opened}</option>
                                                            <option value="if_not_opened">{copy.conditions.if_not_opened}</option>
                                                            <option value="if_clicked">{copy.conditions.if_clicked}</option>
                                                            <option value="if_not_clicked">{copy.conditions.if_not_clicked}</option> {/* ★追加 */}
                                                        </select>
                                                    </div>

                                                    {/* ★ 追加：どのステップを参照するか選ぶドロップダウン */}
                                                    {step.condition_type !== 'always' && (
                                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:pl-7 animate-fadeIn">
                                                            <span className="text-xs text-yellow-800 font-bold">{copy.reference}</span>
                                                            <select
                                                                className="p-1 text-xs border border-yellow-300 rounded bg-white"
                                                                value={step.condition_reference_step_order || idx} // デフォルトは直前のステップ(idx)
                                                                onChange={(e) => updateStep(idx, 'condition_reference_step_order', e.target.value)}
                                                            >
                                                                {formData.steps.slice(0, idx).map((_, prevIdx) => (
                                                                    <option key={prevIdx} value={prevIdx + 1}>
                                                                        {copy.referenceStep(prevIdx + 1)}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <p className="text-[10px] text-yellow-600 italic">
                                                                {copy.referenceNote(step.condition_reference_step_order || idx)}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                                                <div className="bg-gray-100 rounded-full w-8 h-8 flex items-center justify-center font-bold text-gray-500 border border-gray-300 flex-shrink-0">
                                                    {idx + 1}
                                                </div>
                                                
                                                {/* 待機時間設定 */}
                                                <div className="w-full flex-shrink-0 md:w-48">
                                                    <label className="text-xs font-bold text-gray-500 mb-1 block flex items-center gap-1"><ClockIcon/> {copy.waitTime}</label>
                                                    <div className="flex items-center gap-2">
                                                        <input type="number" min="0" className="w-20 p-2 border rounded text-right" value={step.wait_time_hours} onChange={(e) => updateStep(idx, 'wait_time_hours', e.target.value)} />
                                                        <span className="text-sm">{copy.hoursAfter}</span>
                                                    </div>
                                                    <p className="text-xs text-gray-400 mt-1">{copy.immediateNote}</p>
                                                </div>

                                                <div className="hidden md:block text-gray-300">→</div>

                                                {/* アクション設定 (メールソース選択) */}
                                                <div className="flex-grow space-y-2">
                                                    <label className="text-xs font-bold text-gray-500 mb-1 block">{copy.emailSource}</label>
                                                    <div className="relative">
                                                        <select 
                                                            className="w-full p-2 border rounded bg-indigo-50 border-indigo-200 text-indigo-900" 
                                                            value={step.campaign_id} 
                                                            onChange={(e) => updateStep(idx, 'campaign_id', e.target.value)} 
                                                            required
                                                        >
                                                            <option value="">{copy.campaignSelect}</option>
                                                            {campaigns.map(c => (
                                                                <option key={c.id} value={c.id}>[{formatCampaignOptionStatus(c.status, isEnglishBeta)}] {c.title} ({c.subject})</option>
                                                            ))}
                                                        </select>
                                                        <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none text-indigo-400">
                                                            <MailIcon/>
                                                        </div>
                                                    </div>
                                                    <p className="text-xs text-gray-400 mt-1">{copy.campaignNote}</p>
                                                </div>

                                                <button type="button" onClick={() => removeStep(idx)} className="text-gray-400 hover:text-red-500 p-2 self-start md:self-center">
                                                    <TrashIcon />
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* 矢印 (最後以外) */}
                                        <div className="flex justify-center my-2">
                                            <div className="h-6 w-px bg-gray-300"></div>
                                        </div>
                                        <div className="flex justify-center mb-2 text-gray-300">
                                            <ArrowDownIcon />
                                        </div>
                                    </React.Fragment>
                                ))}

                                {/* ステップ追加ボタン */}
                                <div className="flex justify-center mt-4">
                                    <button type="button" onClick={addStep} className="flex items-center gap-2 bg-white border border-dashed border-gray-400 text-gray-600 px-6 py-3 rounded-lg hover:bg-gray-50 hover:border-blue-500 hover:text-blue-600 transition-colors">
                                        <PlusIcon /> {copy.addStep}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 justify-end pt-4 border-t">
                            <label className="flex w-full items-center gap-2 cursor-pointer bg-gray-100 px-4 py-2 rounded hover:bg-gray-200 transition-colors sm:w-auto">
                                <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} className="w-5 h-5 text-blue-600 rounded" />
                                <span className="font-bold text-gray-700">{copy.activate}</span>
                            </label>
                        </div>
                    </form>
                </div>

                <div className="p-4 border-t flex flex-col-reverse gap-3 bg-white sm:flex-row sm:justify-end">
                    <button onClick={onClose} className="w-full px-5 py-2 border rounded text-gray-600 hover:bg-gray-100 font-medium sm:w-auto">{copy.cancel}</button>
                    <button form="scenario-form" type="submit" className="w-full px-8 py-2 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 shadow sm:w-auto">{copy.save}</button>
                </div>
            </div>
        </div>
    );
}

export default function MarketingScenariosPage() {
    const { clientId } = useOutletContext();
    const isEnglishBeta = isEnglishOperatorMode();
    const copy = getScenarioCopy(isEnglishBeta);
    const [scenarios, setScenarios] = useState([]);
    const [campaigns, setCampaigns] = useState([]); // 追加: キャンペーン
    const [forms, setForms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editingScenario, setEditingScenario] = useState(null); // null:一覧, {}:新規, object:編集

    // データ一括取得
    const fetchAllData = useCallback(async () => {
        if (!clientId) return;
        setIsLoading(true);
        try {
            // ★ テンプレート取得を廃止し、キャンペーン取得を追加
            // campaignsのエンドポイントにもclient_idパラメータを渡すことで、管理者が代理ログインした際に
            // そのクライアントのキャンペーンのみを取得するように修正
            const [resScenarios, resForms, resCampaigns] = await Promise.all([
                fetch(`${API_URL}/api/marketing/scenarios?client_id=${clientId}`, { headers: getAuthHeader() }),
                fetch(`${API_URL}/api/marketing/forms?client_id=${clientId}`, { headers: getAuthHeader() }),
                fetch(`${API_URL}/api/marketing/campaigns?client_id=${clientId}`, { headers: getAuthHeader() })
            ]);

            const dataScenarios = await resScenarios.json();
            const dataForms = await resForms.json();
            const dataCampaigns = await resCampaigns.json();

            if (dataScenarios.success) setScenarios(dataScenarios.scenarios);
            if (dataForms.success) setForms(dataForms.forms);
            if (dataCampaigns.success) setCampaigns(dataCampaigns.campaigns);

        } catch (err) {
            console.error("データ取得エラー:", err);
        } finally {
            setIsLoading(false);
        }
    }, [clientId]);

    useEffect(() => { fetchAllData(); }, [fetchAllData]);

    // 詳細データ取得 (編集時)
    const handleEditClick = async (scenario) => {
        try {
            const res = await fetch(`${API_URL}/api/marketing/scenarios/${scenario.id}?client_id=${clientId}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) {
                setEditingScenario({ ...data.scenario, steps: data.steps });
            } else {
                alert(copy.alerts.detailFailed);
            }
        } catch (e) {
            alert(copy.alerts.network);
        }
    };

    const handleSave = async (formData) => {
        try {
            const method = formData.id ? "PUT" : "POST"; // idがあれば更新
            const url = formData.id 
                ? `${API_URL}/api/marketing/scenarios/${formData.id}` 
                : `${API_URL}/api/marketing/scenarios`;

            const res = await fetch(url, {
                method: method,
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ ...formData, client_id: clientId })
            });
            
            const data = await res.json();
            if (data.success) {
                alert(data.message);
                setEditingScenario(null);
                fetchAllData(); // 一覧更新
            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(copy.alerts.deleteConfirm)) return;
        try {
            const res = await fetch(`${API_URL}/api/marketing/scenarios/${id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId })
            });
            if (res.ok) {
                fetchAllData();
            } else {
                alert(copy.alerts.deleteFailed);
            }
        } catch (e) { alert(copy.alerts.network); }
    };

    const getTriggerText = (type, detail) => {
        if (type === 'form_submission') {
            const form = forms.find(f => String(f.id) === String(detail));
            return copy.triggerText.form(form ? form.title : copy.deletedForm);
        }
        // ★追加
        if (type === 'meta_ad_submission') {
            return copy.triggerText.meta(detail || copy.allMetaForms);
        }
        if (type === 'tag_added') return copy.triggerText.tag(detail);
        if (type === 'score_reached') return copy.triggerText.score(detail);
        return copy.triggerText.manual;
    };

    return (
        <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{copy.pageTitle}</h1>
                        <p className="text-sm text-gray-500 mt-1">{copy.pageLead}</p>
                    </div>
                    <button onClick={() => setEditingScenario({})} className="flex w-full items-center justify-center gap-2 bg-blue-600 text-white font-bold py-2 px-4 rounded hover:bg-blue-700 shadow-md transition-colors sm:w-auto">
                        <PlusIcon /> {copy.create}
                    </button>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                    {isLoading ? <LoadingSpinner text={copy.loading} /> : 
                     scenarios.length === 0 ? <div className="p-8 sm:p-12 text-center text-gray-500">{copy.empty}</div> : (
                        <>
                        <div className="divide-y divide-gray-100 md:hidden">
                            {scenarios.map(s => (
                                <div key={`${s.id}-mobile`} className="p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-bold text-gray-800">{s.title}</div>
                                            {s.description && <div className="mt-1 break-words text-xs text-gray-500">{s.description}</div>}
                                        </div>
                                        <span className={`flex shrink-0 items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${s.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                                            {s.is_active ? <><PlayIcon/> {copy.active}</> : <><PauseIcon/> {copy.paused}</>}
                                        </span>
                                    </div>
                                    <div className="mt-3 space-y-2 text-xs text-gray-600">
                                        <p>
                                            <span className="font-semibold text-gray-500">{copy.startCondition}:</span>{' '}
                                            <span className="rounded border border-blue-100 bg-blue-50 px-2 py-1 text-blue-700">
                                                {getTriggerText(s.trigger_type, s.trigger_detail)}
                                            </span>
                                        </p>
                                        <p><span className="font-semibold text-gray-500">{copy.steps}:</span> <span className="font-mono font-bold">{s.step_count}</span></p>
                                        <p><span className="font-semibold text-gray-500">{copy.updated}:</span> {new Date(s.updated_at).toLocaleDateString()}</p>
                                    </div>
                                    <div className="mt-4 grid grid-cols-2 gap-2">
                                        <button onClick={() => handleEditClick(s)} className="flex items-center justify-center gap-1 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-bold text-blue-700">
                                            <EditIcon/> {copy.edit}
                                        </button>
                                        <button onClick={() => handleDelete(s.id)} className="flex items-center justify-center gap-1 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-700">
                                            <TrashIcon/> {copy.delete}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="hidden overflow-x-auto md:block">
                            <table className="w-full text-sm text-left text-gray-600">
                                <thead className="bg-gray-100 text-gray-700 uppercase">
                                    <tr>
                                        <th className="p-4">{copy.headers[0]}</th>
                                        <th className="p-4">{copy.headers[1]}</th>
                                        <th className="p-4">{copy.headers[2]}</th>
                                        <th className="p-4 text-center">{copy.headers[3]}</th>
                                        <th className="p-4">{copy.headers[4]}</th>
                                        <th className="p-4 text-center">{copy.headers[5]}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {scenarios.map(s => (
                                        <tr key={s.id} className="hover:bg-gray-50">
                                            <td className="p-4">
                                                <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold w-fit ${s.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                                                    {s.is_active ? <><PlayIcon/> {copy.active}</> : <><PauseIcon/> {copy.paused}</>}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <div className="font-bold text-gray-800 text-base">{s.title}</div>
                                                {s.description && <div className="text-xs text-gray-500 mt-1">{s.description}</div>}
                                            </td>
                                            <td className="p-4">
                                                <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded border border-blue-100 text-xs">
                                                    {getTriggerText(s.trigger_type, s.trigger_detail)}
                                                </span>
                                            </td>
                                            <td className="p-4 text-center">
                                                <span className="font-mono font-bold text-lg">{s.step_count}</span>
                                            </td>
                                            <td className="p-4">{new Date(s.updated_at).toLocaleDateString()}</td>
                                            <td className="p-4 text-center">
                                                <div className="flex justify-center gap-2">
                                                    <button onClick={() => handleEditClick(s)} className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors" title={copy.edit}><EditIcon/></button>
                                                    <button onClick={() => handleDelete(s.id)} className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors" title={copy.delete}><TrashIcon/></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        </>
                    )}
                </div>
            </div>

            {/* シナリオ編集モーダル */}
            {editingScenario && (
                <ScenarioEditorModal 
                    scenario={editingScenario.id ? editingScenario : null} // 新規ならnull
                    campaigns={campaigns} // 追加
                    forms={forms}
                    onClose={() => setEditingScenario(null)}
                    onSave={handleSave}
                    isEnglishBeta={isEnglishBeta}
                />
            )}
        </div>
    );
}
